import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { extractManagerMensagemInline, extractManagerMotivoTecnico } from 'src/app/core/utils/error.utils';
import { Countdown, formatMMSS } from 'src/app/core/utils/countdown.util';
import {
  OTP_ACESSO_PORTAL_NEGADO_STATE_KEY,
  OtpContaJaPossuiSenhaState,
  OtpEmpresaCandidata,
  OtpFluxo,
  isSolicitarCodigoAmbiguo
} from 'src/app/core/models/otp.model';
import {
  OtpAuthService,
  OtpSolicitarCodigoInput,
  isAcessoPortalNegadoError,
  isContaJaPossuiSenhaError,
  isEntradaInvalidaError,
  isOtpCodigoInvalidoError,
  isOtpEscolhaInvalidaError,
  isOtpReenvioIndisponivelError,
  isRateLimitError
} from 'src/app/core/services/otp-auth.service';

const OTP_MAX_TENTATIVAS = 5;
/** A partir do 2º reenvio concluído com sucesso, o link fica desabilitado pelo resto do desafio (spec §4.9). */
const OTP_MAX_REENVIOS = 2;

/** Contrato mínimo do `app-otp-input` que o controller precisa — evita acoplar ao componente inteiro. */
export interface OtpInputHandle {
  getCode(): string | null | undefined;
  clear(): void;
}

/**
 * Pontos de integração com o componente hospedeiro (`esqueci-senha` ou
 * `primeiro-acesso`) — tudo que só o host sabe decidir (em que `step` do
 * stepper de 4 passos isso aparece, o que fazer com o `resetToken`, etc).
 */
export interface OtpCodigoStepHooks {
  /** Passo 1 resolvido (sem ambiguidade de empresa) — host mostra o passo 2. */
  onAvancarParaCodigo: () => void;
  /**
   * Código validado — chamado SÍNCRONO na resposta (antes do flash de
   * "E-mail confirmado" de 900ms), pra guardar o `resetToken` já com o
   * desafio ainda fresco. Não é o momento de trocar de `step` — isso é
   * `onAvancarParaSenha`, chamado depois do flash.
   */
  onCodigoValidado: (resetToken: string, primeiroNome?: string) => void;
  /** Flash de confirmação terminou — host mostra o passo de senha. */
  onAvancarParaSenha: () => void;
  /** Desafio encerrado (expirado/bloqueado/outro e-mail) — host volta ao passo 1. */
  onVoltarParaIdentificacao: () => void;
  /** Mensagem de erro de página — mesmo campo usado pelos passos seguintes do host. */
  onError: (mensagem: string) => void;
  /** Acesso tardio ao `@ViewChild` do host (só existe depois do view init). */
  getOtpInput: () => OtpInputHandle | undefined;
  /**
   * Conta já tem senha cadastrada (`MANAGER.AUTH.CONTA_JA_POSSUI_SENHA`,
   * spec §4.6-B/§12-C) — OPCIONAL, só `PrimeiroAcessoComponent` implementa.
   * `EsqueciSenhaComponent` deixa `undefined`: o backend nunca emite esse
   * código nesse fluxo, então o ramo não dispara lá por construção. Recebe
   * o valor exatamente como o usuário digitou (identificador OU e-mail) pra
   * pré-preencher o passo 1 de `/esqueci-senha` depois da navegação.
   */
  onContaJaPossuiSenha?: (identificadorOuEmail: OtpContaJaPossuiSenhaState) => void;
}

/**
 * Máquina de estado compartilhada do passo 1 (identificação + sub-estado de
 * escolha de empresa) e passo 2 (código de confirmação) do fluxo OTP.
 *
 * `esqueci-senha` e `primeiro-acesso` têm o MESMO contrato aqui (spec §5) —
 * só o passo 3 em diante (nova senha vs. criar senha + termos) diverge. Esta
 * classe existe para que essa parte comum não precise ser mantida em dois
 * lugares (e divergir, como aconteceu antes — ver fix do ramo de rate limit
 * em `criarSenha`, que vivia só aqui).
 *
 * Não é um `@Injectable` — é instanciada pelo host (`new OtpCodigoStepController(...)`),
 * um por componente, porque guarda estado mutável de uma única sessão de fluxo.
 */
export class OtpCodigoStepController {
  // ── Passo 1 — identificador OU e-mail (D9), nunca os dois ──────────────────
  readonly step1Form;
  loading = false;

  private desafioId: string | null = null;

  // ── Sub-estado do passo 1 — escolha de empresa (ux.md §3.3/§5.12) ─────────
  showEmpresaPanel = false;
  empresaPanelExpirado = false;
  empresasCandidatas: readonly OtpEmpresaCandidata[] = [];
  empresaLoadingRef: string | null = null;
  empresaErrorMessage = '';
  escolhaSegundosRestantes = 0;
  private readonly escolhaCountdown = new Countdown();

  // ── Passo 2 — código ───────────────────────────────────────────────────────
  otpVerificando = false;
  otpInvalido = false;
  otpBloqueado = false;
  otpExpirado = false;
  otpConfirmado = false;
  otpTentativasRestantes = OTP_MAX_TENTATIVAS;
  destinoMascarado = '';

  otpReenviando = false;
  otpReenvioDisponivel = false;
  otpReenvioCotaEsgotada = false;
  otpReenvioSegundosRestantes = 0;
  private reenvios = 0;

  otpSegundosRestantes = 0;
  avisoExpiracaoProxima = false;
  private readonly codigoCountdown = new Countdown();
  private readonly reenvioCountdown = new Countdown();

  /** Handle do flash de 900ms (ux.md §5.11) — cancelado em `destroy()`. */
  private flashHandle: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly fluxo: OtpFluxo,
    fb: FormBuilder,
    private readonly otpAuthService: OtpAuthService,
    private readonly messageService: MessageService,
    private readonly hooks: OtpCodigoStepHooks,
    /** Mensagem de erro do passo 1 — fallback genérico e caso `ENTRADA_INVALIDA`; diverge por flow. */
    private readonly entradaInvalidaMensagem: string,
    /** Só usado pelo ramo `ACESSO_PORTAL_NEGADO` — redireciona pro login com o toast (spec §4.6-C). */
    private readonly router: Router
  ) {
    this.step1Form = fb.nonNullable.group({
      identificador: [''],
      email: ['', [Validators.email, Validators.maxLength(200)]]
    });

    const identificador = this.step1Form.controls.identificador;
    const email = this.step1Form.controls.email;

    // Autofill de grupo (Chrome, gerenciador de senha de terceiro, extensão)
    // pode escrever nos dois `<input>` nativos e disparar `input` nos dois —
    // inclusive no campo que a Angular JÁ desabilitou um instante antes:
    // `disable()` só marca o atributo `disabled` no DOM, o listener do
    // `DefaultValueAccessor` continua registrado e `FormControl.setValue()`
    // não verifica `disabled` (`setUpViewChangePipeline` → `updateControl`).
    // Sem os dois guards abaixo — (1) ignorar reentrada vinda do próprio
    // campo já desabilitado e (2) não deixar `disable()/enable()` emitir
    // `valueChanges` — esse evento residual reentra aqui e desabilita o
    // OUTRO campo também: os dois ficam `disabled`, `step1PodeEnviar` falha
    // nos dois ramos e "Enviar código" nunca habilita (bug reportado pelo
    // Marcos, triagem @Tony 2026-10-24).
    identificador.valueChanges.subscribe((v) => {
      this.aplicarExclusividadeStep1(identificador, email, v);
    });

    email.valueChanges.subscribe((v) => {
      this.aplicarExclusividadeStep1(email, identificador, v);
    });
  }

  /**
   * Regra de exclusividade do passo 1 (D9 — um campo preenchido, nunca os
   * dois): `origem` acabou de mudar de valor; se tiver conteúdo, desabilita
   * e LIMPA `destino` (pedido explícito do Marcos — não deixar valor
   * residual escondido que reaparece se o campo for reabilitado depois);
   * se `origem` esvaziou, reabilita `destino`.
   *
   * `{ emitEvent: false }` em `disable()/enable()/setValue()` é essencial:
   * sem isso, cada mutação re-emite `valueChanges` e reentra nos dois
   * subscribers do construtor, cascateando até desabilitar os dois campos
   * (ver nota acima). `origem.disabled` no topo é a segunda barreira — cobre
   * o evento de view que chega DEPOIS da reentrada já ter desabilitado este
   * mesmo campo (ordem de disparo do autofill não é garantida).
   */
  private aplicarExclusividadeStep1(
    origem: FormControl<string>,
    destino: FormControl<string>,
    valor: string
  ): void {
    if (origem.disabled) return;

    if (valor && valor.trim().length > 0) {
      if (destino.enabled) {
        destino.disable({ emitEvent: false });
        destino.setValue('', { emitEvent: false });
      }
    } else if (destino.disabled) {
      destino.enable({ emitEvent: false });
    }

    // Invariante dura (code review @Tony): nunca os dois campos `disabled`
    // ao mesmo tempo — rede de segurança contra fonte de evento fora de
    // ordem que os dois guards acima não previram (paste simultâneo,
    // terceiro gerenciador de senha). Sem isto o formulário fica num
    // dead-end sem CTA de recuperação visível pro usuário.
    if (origem.disabled && destino.disabled) {
      origem.enable({ emitEvent: false });
    }
  }

  /**
   * Erro sem tipo reconhecido (nem ENTRADA_INVALIDA, nem rate limit, nem um
   * dos códigos do próprio passo) — ex.: 404 de rota, 500 sem envelope, falha
   * de rede. A caixa inline mostra no máximo o `mensagem` estruturado do
   * backend ou o fallback genérico; o texto técnico cru vai só pro toast,
   * nunca pra tela.
   */
  private tratarErroInesperado(err: unknown, fallbackInline: string): string {
    this.messageService.add({
      severity: 'error',
      summary: 'Erro inesperado',
      detail: extractManagerMotivoTecnico(err),
      life: 8000
    });
    return extractManagerMensagemInline(err, fallbackInline);
  }

  destroy(): void {
    this.escolhaCountdown.stop();
    this.codigoCountdown.stop();
    this.reenvioCountdown.stop();
    // Sem isto, sair da tela durante o flash de confirmação dispararia
    // `onAvancarParaSenha()` num componente já destruído (code review
    // @Tony, 2ª passada 2026-10-18).
    if (this.flashHandle !== undefined) {
      clearTimeout(this.flashHandle);
      this.flashHandle = undefined;
    }
  }

  // ── Passo 1 ─────────────────────────────────────────────────────────────────

  get step1PodeEnviar(): boolean {
    const identificador = this.step1Form.controls.identificador;
    const email = this.step1Form.controls.email;
    if (!identificador.disabled && identificador.value.trim().length > 0) {
      return true;
    }
    return !email.disabled && email.value.trim().length > 0 && email.valid;
  }

  /**
   * Valor exatamente como o usuário digitou no passo 1 — identificador OU
   * e-mail, nunca os dois (D9/§4.5), no shape de `OtpContaJaPossuiSenhaState`
   * (campos opcionais, em vez da união discriminada de `OtpSolicitarCodigoInput`)
   * porque alimenta diretamente o `state` do Router no hook
   * `onContaJaPossuiSenha` (spec §12-C item 1) — não vem do backend.
   */
  private valorDigitadoStep1(): Omit<OtpContaJaPossuiSenhaState, 'mensagem'> {
    const identificador = this.step1Form.controls.identificador;
    return !identificador.disabled && identificador.value.trim()
      ? { identificador: identificador.value.trim() }
      : { email: this.step1Form.controls.email.value.trim() };
  }

  solicitarCodigo(): void {
    if (!this.step1PodeEnviar) return;

    this.loading = true;
    this.hooks.onError('');

    const identificador = this.step1Form.controls.identificador;
    const payload: OtpSolicitarCodigoInput =
      !identificador.disabled && identificador.value.trim()
        ? { identificador: identificador.value.trim() }
        : { email: this.step1Form.controls.email.value.trim() };

    this.otpAuthService
      .solicitarCodigo(this.fluxo, payload)
      .subscribe({
        next: (resp) => {
          this.loading = false;
          if (isSolicitarCodigoAmbiguo(resp)) {
            this.abrirPainelEmpresa(resp.desafioId, resp.empresas, resp.escolhaEmpresaEmSegundos);
          } else {
            this.desafioId = resp.desafioId;
            this.irParaPassoCodigo(resp.expiraEmSegundos, resp.reenvioPermitidoEmSegundos);
          }
        },
        error: (err) => {
          this.loading = false;
          if (isContaJaPossuiSenhaError(err) && this.hooks.onContaJaPossuiSenha) {
            // Desvio de fluxo, não erro de preenchimento (spec §12-C item 3)
            // — navega com o valor digitado em vez de mostrar erro inline.
            // Mensagem do backend vai junto pro destino explicar o redirecionamento.
            this.hooks.onContaJaPossuiSenha({
              ...this.valorDigitadoStep1(),
              mensagem: extractManagerMensagemInline(
                err,
                'Esta conta já tem senha cadastrada. Use "Esqueci minha senha" para redefinir.'
              )
            });
          } else if (isEntradaInvalidaError(err)) {
            this.hooks.onError(this.entradaInvalidaMensagem);
          } else if (isRateLimitError(err)) {
            this.hooks.onError(
              'Muitas tentativas em pouco tempo. Aguarde alguns minutos antes de solicitar um novo código.'
            );
          } else {
            this.hooks.onError(this.tratarErroInesperado(err, this.entradaInvalidaMensagem));
          }
        }
      });
  }

  // ── Sub-estado: escolha de empresa ──────────────────────────────────────────

  private abrirPainelEmpresa(
    desafioId: string,
    empresas: readonly OtpEmpresaCandidata[],
    escolhaEmpresaEmSegundos: number
  ): void {
    this.desafioId = desafioId;
    this.empresasCandidatas = empresas;
    this.empresaErrorMessage = '';
    this.empresaPanelExpirado = false;
    this.showEmpresaPanel = true;

    this.escolhaCountdown.onTick = (s) => (this.escolhaSegundosRestantes = s);
    this.escolhaCountdown.onExpire = () => {
      this.empresaPanelExpirado = true;
    };
    this.escolhaCountdown.start(escolhaEmpresaEmSegundos);
    this.escolhaSegundosRestantes = escolhaEmpresaEmSegundos;
  }

  escolherEmpresa(ref: string): void {
    if (!this.desafioId || this.empresaLoadingRef) return;

    this.empresaLoadingRef = ref;
    this.empresaErrorMessage = '';

    this.otpAuthService
      .escolherEmpresa(this.fluxo, this.desafioId, ref)
      .subscribe({
        next: (resp) => {
          this.empresaLoadingRef = null;
          if (isSolicitarCodigoAmbiguo(resp)) {
            // Contrato garante shape normal aqui (spec §5.1-B); defensivo.
            this.empresaErrorMessage = 'Não foi possível confirmar a empresa selecionada.';
            return;
          }
          this.escolhaCountdown.stop();
          this.showEmpresaPanel = false;
          this.empresasCandidatas = [];
          this.irParaPassoCodigo(resp.expiraEmSegundos, resp.reenvioPermitidoEmSegundos);
        },
        error: (err) => {
          this.empresaLoadingRef = null;
          if (isOtpEscolhaInvalidaError(err)) {
            this.empresaErrorMessage =
              'Não foi possível confirmar a empresa selecionada. Volte e solicite um novo código.';
          } else {
            this.empresaErrorMessage = this.tratarErroInesperado(
              err,
              'Não foi possível confirmar a empresa selecionada.'
            );
          }
        }
      });
  }

  /** "← Voltar" do painel de empresa — descarta a escolha em andamento. */
  voltarDoPainelEmpresa(): void {
    this.fecharPainelEmpresa();
  }

  /** CTA do estado "Essa escolha expirou" — idêntico a voltar, nomeado por clareza. */
  voltarEscolhaExpirada(): void {
    this.fecharPainelEmpresa();
  }

  private fecharPainelEmpresa(): void {
    this.escolhaCountdown.stop();
    this.showEmpresaPanel = false;
    this.empresaPanelExpirado = false;
    this.empresasCandidatas = [];
    this.empresaErrorMessage = '';
    this.desafioId = null;
  }

  // ── Passo 2 — código ────────────────────────────────────────────────────────

  private irParaPassoCodigo(expiraEmSegundos: number, reenvioPermitidoEmSegundos: number): void {
    this.otpInvalido = false;
    this.otpBloqueado = false;
    this.otpExpirado = false;
    this.otpConfirmado = false;
    this.otpTentativasRestantes = OTP_MAX_TENTATIVAS;
    this.otpReenvioCotaEsgotada = false;
    this.reenvios = 0;
    this.avisoExpiracaoProxima = false;
    this.hooks.onError('');

    this.iniciarContadorCodigo(expiraEmSegundos);
    this.iniciarContadorReenvio(reenvioPermitidoEmSegundos);
    this.hooks.onAvancarParaCodigo();
  }

  private iniciarContadorCodigo(segundos: number): void {
    this.codigoCountdown.onTick = (s) => (this.otpSegundosRestantes = s);
    this.codigoCountdown.onCrossUnder1Min = () => (this.avisoExpiracaoProxima = true);
    this.codigoCountdown.onExpire = () => {
      this.otpExpirado = true;
    };
    this.codigoCountdown.start(segundos);
    this.otpSegundosRestantes = segundos;
  }

  private iniciarContadorReenvio(segundos: number): void {
    this.otpReenvioDisponivel = false;
    this.reenvioCountdown.onTick = (s) => (this.otpReenvioSegundosRestantes = s);
    this.reenvioCountdown.onExpire = () => (this.otpReenvioDisponivel = true);
    this.reenvioCountdown.start(segundos);
    this.otpReenvioSegundosRestantes = segundos;
  }

  onOtpUserInput(): void {
    if (this.otpInvalido) {
      this.otpInvalido = false;
      this.hooks.onError('');
    }
  }

  onOtpCodeComplete(codigo: string): void {
    this.validarCodigo(codigo);
  }

  confirmarCodigo(): void {
    const codigo = this.hooks.getOtpInput()?.getCode();
    if (codigo) this.validarCodigo(codigo);
  }

  get otpPodeConfirmar(): boolean {
    return !!this.hooks.getOtpInput()?.getCode() && !this.otpVerificando;
  }

  private validarCodigo(codigo: string): void {
    if (this.otpVerificando || !this.desafioId) return;

    this.otpVerificando = true;
    this.hooks.onError('');

    this.otpAuthService.validarCodigo(this.fluxo, this.desafioId, codigo).subscribe({
      next: (resp) => {
        this.destinoMascarado = resp.destinoMascarado;
        this.otpConfirmado = true;
        // O servidor ACEITOU o código — se o contador local tinha acabado de
        // cruzar 00:00 no meio da requisição, `otpExpirado` ficaria true e o
        // painel "Esse código expirou" cobriria o flash de confirmação de um
        // código que foi validado (code review @Tony, 2ª passada 2026-10-18).
        // Quem decide é a resposta, nunca o relógio do cliente.
        this.otpExpirado = false;
        this.codigoCountdown.stop();
        this.reenvioCountdown.stop();
        // Guarda o resetToken já aqui (síncrono) — o flash abaixo é só UI,
        // não pode ser o que condiciona ter ou não um token válido.
        this.hooks.onCodigoValidado(resp.resetToken, resp.primeiroNome);
        this.flashHandle = setTimeout(() => {
          this.flashHandle = undefined;
          this.otpConfirmado = false;
          this.otpVerificando = false;
          this.hooks.onAvancarParaSenha();
        }, 900);
      },
      error: (err) => {
        this.otpVerificando = false;

        if (isAcessoPortalNegadoError(err)) {
          // Código conferiu, mas a conta não tem acesso ao portal (spec
          // §4.6-C) — o desafio já foi consumido no backend, não existe
          // reenvio possível. Decisão do Marcos: não fica no passo 1 deste
          // fluxo — volta direto pro login, com a mensagem indo via state
          // do Router (mesmo padrão do CONTA_JA_POSSUI_SENHA) pro toast
          // aparecer lá, não aqui.
          const mensagem = extractManagerMensagemInline(
            err,
            'Você não tem acesso ao portal. Fale com o administrador da sua empresa.'
          );
          this.router.navigate(['/login'], {
            state: { [OTP_ACESSO_PORTAL_NEGADO_STATE_KEY]: { mensagem } }
          });
          return;
        }

        if (isOtpCodigoInvalidoError(err)) {
          this.otpTentativasRestantes = Math.max(0, this.otpTentativasRestantes - 1);
          this.hooks.getOtpInput()?.clear();

          if (this.otpTentativasRestantes <= 0) {
            this.otpBloqueado = true;
            this.codigoCountdown.stop();
            this.reenvioCountdown.stop();
          } else {
            this.otpInvalido = true;
            this.hooks.onError(`Código incorreto. Restam ${this.otpTentativasRestantes} tentativas.`);
          }
        } else if (isRateLimitError(err)) {
          this.hooks.onError(
            'Muitas tentativas em pouco tempo. Aguarde alguns minutos antes de solicitar um novo código.'
          );
        } else {
          this.hooks.onError(this.tratarErroInesperado(err, 'Não foi possível confirmar o código.'));
        }
      }
    });
  }

  /**
   * Único predicado de "o link de reenviar está clicável" — o template usa
   * este getter em vez de recombinar as três flags, pra que a regra do
   * cooldown + cota (ux.md §5.10) viva num só lugar.
   */
  get otpReenvioPermitido(): boolean {
    return this.otpReenvioDisponivel && !this.otpReenviando && !this.otpReenvioCotaEsgotada;
  }

  /**
   * Label do próprio link de reenvio — durante o cooldown, o contador vira
   * texto do link ("Reenviar código em 42s") em vez de um hint à parte;
   * some e volta a "Reenviar código" assim que o cooldown zera.
   */
  get otpReenvioLabel(): string {
    if (!this.otpReenvioCotaEsgotada && !this.otpReenvioDisponivel) {
      return `Reenviar código em ${this.otpReenvioSegundosRestantes}s`;
    }
    return 'Reenviar código';
  }

  reenviarCodigo(): void {
    if (!this.desafioId || !this.otpReenvioPermitido) return;

    this.otpReenviando = true;
    this.hooks.onError('');

    this.otpAuthService
      .reenviarCodigo(this.fluxo, this.desafioId)
      .subscribe({
        next: (resp) => {
          this.otpReenviando = false;
          this.reenvios += 1;
          this.hooks.getOtpInput()?.clear();
          this.otpInvalido = false;
          this.otpExpirado = false;
          this.avisoExpiracaoProxima = false;
          this.iniciarContadorCodigo(resp.expiraEmSegundos);
          this.iniciarContadorReenvio(resp.reenvioPermitidoEmSegundos);
          if (this.reenvios >= OTP_MAX_REENVIOS) {
            this.otpReenvioCotaEsgotada = true;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Código reenviado',
            detail: 'Novo código enviado. Confira seu e-mail.',
            life: 4000
          });
        },
        error: (err) => {
          this.otpReenviando = false;

          if (isRateLimitError(err)) {
            this.hooks.onError(
              'Muitas tentativas em pouco tempo. Aguarde alguns minutos antes de solicitar um novo código.'
            );
          } else if (isOtpReenvioIndisponivelError(err)) {
            // O desafio morreu entre o countdown local e o servidor confirmar
            // (relógio dessincronizado, cota batida em outra aba, etc). Backend
            // exige desafio vivo pra reenviar — não há novo reenvio possível
            // aqui, então cai no mesmo estado "código expirado" (que agora
            // tem uma saída real: volta ao passo 1, não tenta de novo).
            this.otpExpirado = true;
            this.codigoCountdown.stop();
            this.reenvioCountdown.stop();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Não foi possível reenviar. Tente novamente.',
              life: 4000
            });
          }
        }
      });
  }

  /** Painel "Limite de tentativas atingido" — volta ao passo 1 pré-preenchido. */
  solicitarNovoCodigoAposBloqueio(): void {
    this.voltarAoPasso1();
  }

  /**
   * Painel "Esse código expirou" — o desafio já está morto no backend (ele
   * exige desafio vivo pra aceitar reenvio), então o CTA NÃO chama
   * `reenviar-codigo` (isso sempre voltaria 422 `OTP_REENVIO_INDISPONIVEL` —
   * beco sem saída real). Em vez disso volta ao passo 1 pra pedir um código
   * novo do zero, igual ao painel de bloqueio por tentativas.
   */
  reenviarAposExpiracao(): void {
    this.voltarAoPasso1();
  }

  tentarOutraEntrada(): void {
    this.voltarAoPasso1();
  }

  private voltarAoPasso1(): void {
    this.codigoCountdown.stop();
    this.reenvioCountdown.stop();
    this.desafioId = null;
    this.otpBloqueado = false;
    this.otpExpirado = false;
    this.otpInvalido = false;
    this.otpReenvioCotaEsgotada = false;
    this.hooks.onError('');
    this.hooks.onVoltarParaIdentificacao();
  }

  formatMMSS(s: number): string {
    return formatMMSS(s);
  }
}
